import { Box, Grid, Typography, CircularProgress } from "@mui/material";
import { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Product } from "../../redux/types/productTypes";
import { RootState } from "../../redux/store";
import { CenteredLoading } from "../common/CenteredLoading";
import {
  fetchWishlistProducts,
  removeWishlist,
  PAGE_SIZE,
} from "../../redux/actions/wishlistAction";
import {
  createCart,
  fetchCartProducts,
  PAGE_SIZE as CART_PAGE_SIZE,
} from "../../redux/actions/cartAction";
import { ProfileProductCard } from "../product/ProfileProductCard";
import SnackbarAlert, { SnackbarRef } from "../common/SnackbarAlert";
import { DrawerModal } from "../common/DrawerModal";
import { SocialMediaShare } from "../product/SocialMediaShare";
import { BASE_URL_WEB_APP } from "../../constants";

import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";

interface IWishlistProps {
  profileId: string | number | null;
}

export const Wishlist: React.FC<IWishlistProps> = ({ profileId }) => {
  const dispatch = useDispatch();
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [lastDocId, setLastDocId] = useState<string | null>(null);
  const [isLoadingFromScroll, setIsLoadingFromScroll] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedProductForShare, setSelectedProductForShare] =
    useState<Product | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "warning" | "info"
  >("success");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [updatingItems, setUpdatingItems] = useState<Set<number>>(new Set());
  const [deletingItems, setDeletingItems] = useState<Set<number>>(new Set());
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);

  const snackbarRef = useRef<SnackbarRef>(null);

  const { loading, wishlistData, error } = useSelector(
    (state: RootState) => state.wishlists
  );
  const { products } = wishlistData;

  // reset + fetch on tab change
  useEffect(() => {
    setAllProducts([]);
    setLastDocId(null);
    setHasInitiallyLoaded(false);
    if (profileId) {
      dispatch(fetchWishlistProducts(Number(profileId), "", PAGE_SIZE));
    }
  }, [dispatch, profileId]);

  // Append new results - replace on reset, append on scroll
  useEffect(() => {
    if (!loading && products.length > 0) {
      // If allProducts is empty or lastDocId is null, this is a fresh load - replace instead of append
      const shouldReplace = allProducts.length === 0 || lastDocId === null;

      const unique = shouldReplace
        ? products.filter(
            (item, index, self) =>
              index === self.findIndex((i) => i.documentId === item.documentId)
          )
        : [...allProducts, ...products].filter(
            (item, index, self) =>
              index === self.findIndex((i) => i.documentId === item.documentId)
          );

      setAllProducts(unique);

      // Get last document ID from wishlist data
      const lastWishlist =
        wishlistData.wishlists[wishlistData.wishlists.length - 1];
      if (lastWishlist?.documentId) {
        setLastDocId(lastWishlist.documentId);
      }

      // Reset scroll loading flag
      setIsLoadingFromScroll(false);

      // Mark initial load as complete
      setHasInitiallyLoaded(true);
    }
  }, [products, loading, allProducts, wishlistData.wishlists, lastDocId]);

  // Also mark as loaded when loading finishes with empty results (empty wishlist)
  useEffect(() => {
    if (!loading && products.length === 0 && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true);
    }
  }, [loading, products.length, hasInitiallyLoaded]);

  // Infinite scroll logic
  const handleScroll = useCallback(() => {
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
    if (
      scrollTop + clientHeight >= scrollHeight - 100 &&
      !loading &&
      !isLoadingFromScroll &&
      lastDocId !== null &&
      allProducts.length > 0 // Only trigger if we have items to scroll through
    ) {
      setIsLoadingFromScroll(true);
      dispatch(
        fetchWishlistProducts(Number(profileId), lastDocId || "", PAGE_SIZE)
      );
    }
  }, [
    dispatch,
    profileId,
    lastDocId,
    loading,
    isLoadingFromScroll,
    allProducts.length,
  ]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Handle snackbar display
  useEffect(() => {
    if (showSnackbar && snackbarRef.current) {
      snackbarRef.current.showSnackbar(snackbarMessage, snackbarSeverity);
      setShowSnackbar(false);
    }
  }, [showSnackbar, snackbarMessage, snackbarSeverity]);

  // Listen for successful remove operations
  useEffect(() => {
    if (!loading && !error) {
      // This will trigger when a remove operation completes successfully
      // We'll use a ref to track if we just removed an item
      const justRemoved = sessionStorage.getItem("justRemovedItem");
      if (justRemoved === "true") {
        sessionStorage.removeItem("justRemovedItem");
        setSnackbarMessage("Removed from wishlist");
        setSnackbarSeverity("success");
        setShowSnackbar(true);
      }
    }
  }, [loading, error]);

  // handlers
  const handleRemoveFromWishlist = async (productId: number) => {
    if (!profileId) return;

    // Add to deleting set
    setDeletingItems((prev) => new Set(prev).add(productId));

    try {
      // Set flag to indicate we just removed an item
      sessionStorage.setItem("justRemovedItem", "true");

      // Dispatch and update state
      await dispatch(removeWishlist(Number(profileId), productId) as any);
      setAllProducts((prev) => prev.filter((p) => p.createdTime !== productId));
    } finally {
      // Remove from deleting set
      setDeletingItems((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  const handleAddToCart = async (productId: number) => {
    if (!profileId) return;

    // Find the product to check stock
    const product = allProducts.find((p) => p.createdTime === productId);
    if (!product) return;

    // Check if item is out of stock
    if (product.productQuantity === 0) {
      setSnackbarMessage("Sorry, item out of stock");
      setSnackbarSeverity("warning");
      setShowSnackbar(true);
      return;
    }

    // Add to updating set
    setUpdatingItems((prev) => new Set(prev).add(productId));

    try {
      // Add to cart
      await dispatch(createCart(Number(profileId), productId, 1) as any);

      // Remove from wishlist
      await dispatch(removeWishlist(Number(profileId), productId) as any);

      // Refetch cart to update badge
      dispatch(fetchCartProducts(Number(profileId), "", CART_PAGE_SIZE) as any);

      // Refetch wishlist to sync with server
      await dispatch(
        fetchWishlistProducts(Number(profileId), "", PAGE_SIZE) as any
      );

      // Show success message
      setSnackbarMessage("Added to your cart");
      setSnackbarSeverity("success");
      setShowSnackbar(true);

      // Remove item from local state immediately (it's already removed from Firebase)
      // The item won't appear in the refetched products array
      setAllProducts((prev) => prev.filter((p) => p.createdTime !== productId));

      // Remove from updating set after a short delay to allow success message to be seen
      // before re-enabling the button
      setTimeout(() => {
        setUpdatingItems((prev) => {
          const next = new Set(prev);
          next.delete(productId);
          return next;
        });
      }, 1000);
    } catch (error: any) {
      setSnackbarMessage(error?.message || "Failed to add item to cart");
      setSnackbarSeverity("error");
      setShowSnackbar(true);

      // Remove from updating set on error
      setUpdatingItems((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });

      // Refetch wishlist on error to sync state
      await dispatch(
        fetchWishlistProducts(Number(profileId), "", PAGE_SIZE) as any
      );
    }
  };

  const handleShare = (product: Product) => {
    setSelectedProductForShare(product);
    setIsShareModalOpen(true);
  };

  const openShareModal = () => setIsShareModalOpen(true);
  const closeShareModal = (closeAfterSuccess = false) => {
    setIsShareModalOpen(false);
    if (typeof closeAfterSuccess === "boolean" && closeAfterSuccess === true) {
      setSnackbarMessage("Link copied");
      setSnackbarSeverity("success");
      setShowSnackbar(true);
    }
  };

  // Only show full page loader on initial tab load
  if (!hasInitiallyLoaded && loading) {
    return <CenteredLoading />;
  }

  return (
    <>
      <Box>
        <Typography variant="h6" fontWeight="bold" mb={2}>
          My Wishlist
        </Typography>
        <Grid container spacing={2}>
          {allProducts.length > 0 ? (
            allProducts.map((item, idx) => {
              const isUpdating = updatingItems.has(item.createdTime);
              const isDeleting = deletingItems.has(item.createdTime);
              return (
                <Grid item xs={6} sm={4} md={3} lg={2} key={idx}>
                  <ProfileProductCard
                    imageLink={item.productImage}
                    title={item.productName}
                    price={item.productPrice}
                    onView={() => window.open(item.productUrl, "_blank")}
                    actionButtons={[
                      {
                        title: "Add to Cart",
                        icon: isUpdating ? (
                          <CircularProgress size={18} />
                        ) : (
                          <AddShoppingCartIcon fontSize="small" />
                        ),
                        onClick: () => handleAddToCart(item.createdTime),
                        disabled: isUpdating || isDeleting,
                      },
                      {
                        title: "Remove",
                        icon: isDeleting ? (
                          <CircularProgress size={18} />
                        ) : (
                          <DeleteOutlineIcon fontSize="small" />
                        ),
                        onClick: () =>
                          handleRemoveFromWishlist(item.createdTime),
                        disabled: isDeleting || isUpdating,
                      },
                      {
                        title: "View",
                        icon: <VisibilityOutlinedIcon fontSize="small" />,
                        onClick: () => window.open(item.productUrl, "_blank"),
                      },
                      {
                        title: "Share",
                        icon: <ShareOutlinedIcon fontSize="small" />,
                        onClick: () => handleShare(item),
                      },
                    ]}
                  />
                </Grid>
              );
            })
          ) : (
            <Grid item xs={12}>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
                sx={{ color: "text.secondary" }}
              >
                <Typography variant="h6" textAlign="center">
                  Your wishlist is empty.
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>

        {loading && allProducts.length >= PAGE_SIZE && (
          <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress />
          </Box>
        )}
      </Box>

      <SnackbarAlert ref={snackbarRef} />

      <DrawerModal
        isOpen={isShareModalOpen}
        onClose={closeShareModal}
        onOpen={openShareModal}
        anchor={"bottom"}
      >
        {selectedProductForShare && (
          <SocialMediaShare
            shareURL={`${BASE_URL_WEB_APP}${selectedProductForShare.productUrl}`}
            title={selectedProductForShare.productName}
            closeShareModal={closeShareModal}
          />
        )}
      </DrawerModal>
    </>
  );
};
